import React from 'react'

import { isNil } from 'ramda'

import { A, Colorway, Div, H4, P, Span } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { hasGates } from 'utils/access'
import { newConversation } from 'utils/widget'

import { CHART_NO_DATA_TEXT } from '../constants'

import './style.css'

const ERROR_HEADER = 'Something went wrong'
const ERROR_DESCRIPTION = 'We’re unable to retrieve data for this report.'

interface IChartStateProps {
  header?: string
  description?: React.ReactNode
  imageSrc?: string
  imageHeight?: any
  imageWidth?: any
}

const ChartState = ({
  header,
  description,
  imageSrc,
  imageWidth = 'auto',
  imageHeight = 'auto',
}: IChartStateProps) => (
  <Div className="chart-state-container">
    {imageSrc && (
      <img
        className="chart-state-image"
        src={imageSrc}
        alt="No data"
        style={{ width: imageWidth, height: imageHeight }}
      />
    )}
    {header && <Div className="chart-state-header">{header}</Div>}
    {description && (
      <Div className="chart-state-text chart-state-description">
        {description}
      </Div>
    )}
  </Div>
)

export default ChartState

interface IChartNoDataStateProps {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  description?: React.ReactNode
  icon?: any
}

export const CardsNoDataState = () => {
  return (
    <Div className="chart-no-data-state chart-no-data-state--cards">
      <Colorway type="4" size="small">
        <Icon name="cw-marketplace-clp" />
      </Colorway>
      <Span>{CHART_NO_DATA_TEXT}</Span>
    </Div>
  )
}

export const ChartNoDataState = ({
  title,
  subtitle,
  description,
  icon,
}: IChartNoDataStateProps) => {
  return (
    <Div className="chart-no-data-state">
      <Div>
        {icon ?? (
          <Colorway type="4">
            <Icon name="cw-empty-search" />
          </Colorway>
        )}
      </Div>
      <H4>{isNil(title) ? CHART_NO_DATA_TEXT : title}</H4>
      {!!subtitle && <P className="chart-no-data-subtitle">{subtitle}</P>}
      {!!description && (
        <P className="chart-no-data-description">{description}</P>
      )}
    </Div>
  )
}

const renderErrorCTAs = (onRetryClick) => {
  const contactAction = hasGates('premium_drift') ? (
    `${onRetryClick ? 'c' : 'C'}ontact support for assistance`
  ) : (
    <A onClick={newConversation}>{onRetryClick ? 'c' : 'C'}hat with us</A>
  )

  if (onRetryClick) {
    return (
      <>
        <A onClick={() => onRetryClick()}>Refresh</A> or {contactAction}
      </>
    )
  }

  return contactAction
}

interface IChartErrorStateProps {
  onRetryClick?: () => void
}

export const ChartErrorState = ({ onRetryClick }: IChartErrorStateProps) => (
  <ChartState
    header={ERROR_HEADER}
    description={
      <>
        {ERROR_DESCRIPTION}
        <br />
        {renderErrorCTAs(onRetryClick)}
      </>
    }
    imageSrc={require('images/chart-error.svg')}
  />
)
